// Render the pages of a PDF file to image data URLs (client-side), so uploaded PDFs
// flow through the same crop + AI-scan pipeline as photos. Uses the app's established
// pdfjs-dist setup (see src/features/pdf-editor/engine/pdfEngine.js); the
// Promise.withResolvers polyfill loaded in src/main.jsx must run first (it does).
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

export function isPdf(file) {
  return !!(file && (file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '')));
}

// Convert one PDF File into an array of PNG data URLs (one per page). `maxPages`
// guards against pathological documents; `targetWidth` keeps fine print (NID digits,
// Bangla names) legible for the vision model without bloating payloads.
export async function pdfToImages(file, { maxPages = 12, targetWidth = 1600 } = {}) {
  const buf = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
  const pageCount = Math.min(pdf.numPages, maxPages);
  const images = [];
  for (let n = 1; n <= pageCount; n++) {
    const page = await pdf.getPage(n);
    const base = page.getViewport({ scale: 1 });
    const scale = Math.min(3, Math.max(1, targetWidth / base.width));
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: ctx, viewport }).promise;
    images.push(canvas.toDataURL('image/png'));
    page.cleanup();
  }
  try { await pdf.destroy(); } catch { /* ignore */ }
  return images;
}
